import { useRef, useState } from "react";
import { Camera, X, Upload } from "lucide-react";

/**
 * PhotoUploader — captures multiple photos (camera on mobile / file picker on desktop).
 * Photos are resized client-side to max 1024px width and returned as base64 data URLs.
 */
export default function PhotoUploader({ value = [], onChange, maxPhotos = 6, testid = "photo-uploader" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const resizeImage = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxW = 1024;
          const scale = Math.min(1, maxW / img.width);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.75));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFiles = async (files) => {
    setBusy(true);
    const arr = [...value];
    for (const file of Array.from(files)) {
      if (arr.length >= maxPhotos) break;
      try {
        const b64 = await resizeImage(file);
        arr.push(b64);
      } catch (e) {
        console.warn("Falha ao processar foto:", file?.name, e);
      }
    }
    onChange?.(arr);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAt = (idx) => {
    const arr = value.filter((_, i) => i !== idx);
    onChange?.(arr);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-caps">Fotos ({value.length}/{maxPhotos})</span>
        <button
          type="button"
          className="btn btn-secondary text-xs"
          onClick={() => fileRef.current?.click()}
          disabled={busy || value.length >= maxPhotos}
          data-testid={`${testid}-add`}
        >
          <Camera size={14} /> Adicionar foto
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
      {value.length === 0 ? (
        <div
          className="border border-dashed p-6 text-center text-sm cursor-pointer"
          style={{ borderColor: "var(--border-strong)", color: "var(--text-mute)" }}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={22} className="mx-auto mb-2" />
          Nenhuma foto anexada. Toque para adicionar.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {value.map((src, i) => (
            <div key={i} className="relative border" style={{ borderColor: "var(--border-strong)" }}>
              <img src={src} alt={`foto ${i + 1}`} className="w-full h-24 object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 p-1"
                style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
        As fotos são redimensionadas para 1024px de largura e armazenadas junto do registro.
      </div>
    </div>
  );
}
